import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { GlassCard } from '@/components/ui/glass-card'
import { useLuxeHero, useLuxeReveal } from '@gjl/motion/css'
import { ArrowRight, Building, Calendar, MapPin } from 'lucide-react'

export default function TrackRecordPage() {
  const heroRef = useLuxeHero<HTMLDivElement>(0.2)
  const dealRef = useLuxeReveal<HTMLDivElement>('.reveal')
  const lessonsRef = useLuxeReveal<HTMLDivElement>('.reveal')

  return (
    <div className="bg-background text-foreground">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <div className="bg-gradient-dark-gold absolute inset-0" />
        <div ref={heroRef} className="relative z-10 mx-auto max-w-4xl px-4 text-center">
          <p className="text-primary mb-6 text-lg font-medium tracking-wide">Track record</p>
          <h1 className="text-fluid-h1 text-foreground mb-6">Our Portfolio</h1>
          <p className="text-fluid-body-lg text-muted-foreground mx-auto max-w-2xl">
            Every property in the portfolio, with full financials.
          </p>
        </div>
      </section>

      {/* Portfolio Overview */}
      <section className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="mb-12 grid gap-6 md:grid-cols-3">
            <GlassCard className="text-center">
              <div className="text-primary mb-2 text-4xl font-bold">1</div>
              <div className="text-muted-foreground">Properties Acquired</div>
            </GlassCard>
            <GlassCard className="text-center">
              <div className="text-primary mb-2 text-4xl font-bold">$170K</div>
              <div className="text-muted-foreground">Total Investment</div>
            </GlassCard>
            <GlassCard className="text-center">
              <div className="text-primary mb-2 text-4xl font-bold">2</div>
              <div className="text-muted-foreground">Units</div>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* Deal Intro */}
      <section className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <GlassCard accent>
            <h2 className="text-fluid-h3 text-foreground mb-4">Deal #1: The One That Started It All</h2>
            <p className="text-fluid-body-lg text-muted-foreground">
              Our first acquisition: a fully occupied duplex on Cleveland's west side, cash-flowing from close.
            </p>
          </GlassCard>
        </div>
      </section>

      {/* Deal Details */}
      <section ref={dealRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-5xl">
          <GlassCard className="reveal overflow-hidden">
            <div className="grid gap-8 md:grid-cols-2">
              {/* Image */}
              <div className="relative aspect-[4/3] overflow-hidden rounded-xl">
                <img
                  src="/duplex-westblvd.webp"
                  alt="Front of duplex in Cleveland's West Boulevard neighborhood"
                  className="h-full w-full object-cover"
                />
                <div className="from-background/60 absolute inset-0 bg-gradient-to-t to-transparent" />
                <div className="text-foreground absolute bottom-4 left-4 flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4" />
                  <span>Closed April 17, 2025</span>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-6">
                <div>
                  <div className="text-primary mb-2 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    <span className="text-sm font-medium">West Boulevard, Cleveland, OH</span>
                  </div>
                  <h3 className="text-foreground text-2xl font-bold md:text-3xl">West Boulevard Duplex</h3>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="glass-subtle rounded-lg p-4">
                    <div className="text-muted-foreground mb-1 text-sm">Type</div>
                    <div className="text-foreground font-semibold">Duplex</div>
                  </div>
                  <div className="glass-subtle rounded-lg p-4">
                    <div className="text-muted-foreground mb-1 text-sm">Size</div>
                    <div className="text-foreground font-semibold">2,348 sq ft</div>
                  </div>
                  <div className="glass-subtle rounded-lg p-4">
                    <div className="text-muted-foreground mb-1 text-sm">Units</div>
                    <div className="text-foreground font-semibold">3 bed / 1 bath each</div>
                  </div>
                  <div className="glass-subtle rounded-lg p-4">
                    <div className="text-muted-foreground mb-1 text-sm">Price</div>
                    <div className="text-primary font-semibold">$170,000</div>
                  </div>
                </div>
              </div>
            </div>
          </GlassCard>
        </div>
      </section>

      {/* Financials */}
      <section className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="mb-12 text-center">
            <h2 className="text-fluid-h2 text-foreground mb-4">The Numbers</h2>
            <p className="text-muted-foreground text-lg">Conservative underwriting, real-world performance</p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <GlassCard className="gold-accent-left pl-8">
              <h3 className="text-foreground mb-4 text-lg font-semibold">Purchase Details</h3>
              <ul className="text-muted-foreground space-y-3">
                <li className="flex justify-between">
                  <span>Purchase Price</span>
                  <span className="text-foreground font-semibold">$170,000</span>
                </li>
                <li className="flex justify-between">
                  <span>Down Payment (25%)</span>
                  <span className="text-foreground font-semibold">$42,500</span>
                </li>
                <li className="flex justify-between">
                  <span>Financing</span>
                  <span className="text-foreground font-semibold">Conventional</span>
                </li>
                <li className="flex justify-between">
                  <span>Management</span>
                  <span className="text-foreground font-semibold">Urban Progressive PM</span>
                </li>
              </ul>
            </GlassCard>

            <GlassCard className="gold-accent-left pl-8">
              <h3 className="text-foreground mb-4 text-lg font-semibold">Current Metrics (Q2 2025)</h3>
              <ul className="text-muted-foreground space-y-3">
                <li className="flex justify-between">
                  <span>Monthly Rent</span>
                  <span className="text-foreground font-semibold">$2,400 total</span>
                </li>
                <li className="flex justify-between">
                  <span>Per Unit</span>
                  <span className="text-foreground font-semibold">$1,200</span>
                </li>
                <li className="flex justify-between">
                  <span>Occupancy</span>
                  <span className="text-primary font-semibold">100% occupied (2 of 2 units)</span>
                </li>
                <li className="flex justify-between">
                  <span>Status</span>
                  <span className="text-foreground font-semibold">Stabilized</span>
                </li>
              </ul>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* Lessons Learned */}
      <section ref={lessonsRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <div className="mb-12 text-center">
            <h2 className="text-fluid-h2 text-foreground mb-4">What We Learned</h2>
            <p className="text-muted-foreground text-lg">Every deal teaches us something valuable</p>
          </div>

          <div className="reveal space-y-6">
            <GlassCard hover>
              <div className="flex gap-6">
                <div className="text-primary shrink-0 text-3xl font-bold">1</div>
                <div>
                  <h3 className="text-foreground mb-2 text-xl font-semibold">
                    The Stress-Tested Cushion Earned Its Keep
                  </h3>
                  <p className="text-muted-foreground">
                    We budgeted for longer vacancy and higher repair costs than "industry standards" suggested. That
                    cushion absorbed the initial turnover period without putting cash flow at risk.
                  </p>
                </div>
              </div>
            </GlassCard>

            <GlassCard hover>
              <div className="flex gap-6">
                <div className="text-primary shrink-0 text-3xl font-bold">2</div>
                <div>
                  <h3 className="text-foreground mb-2 text-xl font-semibold">
                    Property Management Is Worth Every Penny
                  </h3>
                  <p className="text-muted-foreground">
                    Delegating to professionals let me focus on deal analysis and investor relations instead of handling
                    tenant calls at 2 AM.
                  </p>
                </div>
              </div>
            </GlassCard>

            <GlassCard hover>
              <div className="flex gap-6">
                <div className="text-primary shrink-0 text-3xl font-bold">3</div>
                <div>
                  <h3 className="text-foreground mb-2 text-xl font-semibold">Boring Works</h3>
                  <p className="text-muted-foreground">
                    No one will write articles about this duplex. It won't make headlines. But it generates real income,
                    month after month. That's the whole point.
                  </p>
                </div>
              </div>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <GlassCard accent className="text-center">
            <Building className="text-primary mx-auto mb-6 h-12 w-12" />
            <h2 className="text-fluid-h3 text-foreground mb-4">More Deals Coming Soon</h2>
            <p className="text-muted-foreground mx-auto mb-8 max-w-2xl text-lg">
              We're actively underwriting our next acquisition. Join the investor list to get first access when it's
              ready.
            </p>
            <Link to="/contact">
              <Button size="xl">
                Join the Investor List
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </GlassCard>
        </div>
      </section>
    </div>
  )
}
